import ExcelJS from 'exceljs'
import type { EmployeeSchedule, ScheduleDay } from '../types/schedule.types'

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

const COLORS = {
  green: 'DCFCE7',       // рабочий день
  indigo: 'E0E7FF',      // ночная смена
  gray: 'F3F4F6',        // выходной
  red: 'FEE2E2',         // праздник
  yellow: 'FEF9C3',      // сокращённый
  amber: 'FDE68A',       // исключение (рабочий)
  amberLight: 'FEF3C7',  // исключение (выходной)
  greenSummary: 'DCFCE7', // столбец Раб.
  graySummary: 'F3F4F6',  // столбец Вых.
  blueSummary: 'DBEAFE',  // столбец ШР (штатный график 1С)
  emeraldSummary: 'D1FAE5', // столбец Факт (отработано по отметкам)
  weekendText: 'EF4444',
  headerBg: 'F3F4F6',
  indigoText: '4338CA',
  greenText: '16A34A',
  blueText: '1D4ED8',
  emeraldText: '047857',
}

interface ExportScheduleParams {
  employees: EmployeeSchedule[]
  dates: Date[]
  departmentName: string
  year: number
  month: number // 0-indexed
}

function formatTime(time: string | null | undefined): string {
  if (!time) return ''
  const value = time.toString()
  const timePart = value.includes('T') ? value.split('T')[1] : value
  return timePart.slice(0, 5)
}

function formatDateStr(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

function findScheduleDay(employee: EmployeeSchedule, date: Date): ScheduleDay | undefined {
  const dateStr = formatDateStr(date)
  return employee.schedule.find((s) => s.date === dateStr)
}

function getCellColor(day: ScheduleDay): string {
  if (day.source === 'schedule_exception') {
    return day.is_working_day ? COLORS.amber : COLORS.amberLight
  }
  if (!day.is_working_day || day.schedule_type === 'day_off') {
    return COLORS.gray
  }
  switch (day.schedule_type) {
    case 'night_shift': return COLORS.indigo
    case 'holiday': return COLORS.red
    case 'reduced': return COLORS.yellow
    default: return COLORS.green
  }
}

function getCellText(day: ScheduleDay): string {
  if (!day.is_working_day) return 'Вых'
  const start = formatTime(day.shift_start)
  const end = formatTime(day.shift_end)
  if (start && end) return `${start}-${end}`
  if (start) return start
  return ''
}

export async function exportScheduleToExcel(params: ExportScheduleParams): Promise<void> {
  const { employees, dates, departmentName, year, month } = params

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'TCO Admin'
  workbook.created = new Date()

  const worksheet = workbook.addWorksheet('График', {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }],
  })

  const totalColumns = 1 + dates.length + 4 // name + days + ШР + Раб. + Факт + Вых.

  // --- Title row ---
  const titleRow = worksheet.addRow([
    `График работы — ${departmentName} — ${MONTH_NAMES[month]} ${year}`,
  ])
  worksheet.mergeCells(1, 1, 1, Math.min(15, totalColumns))
  titleRow.font = { size: 14, bold: true }
  titleRow.height = 28

  // --- Header row 1: weekday names ---
  const header1Data: string[] = ['Сотрудник']
  for (const date of dates) {
    header1Data.push(WEEKDAY_NAMES[date.getDay()])
  }
  header1Data.push('ШР', 'Раб.', 'Факт', 'Вых.')
  const header1 = worksheet.addRow(header1Data)
  header1.font = { bold: true, size: 9 }
  header1.height = 18

  header1.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = thinBorder()
    cell.fill = solidFill(COLORS.headerBg)

    if (colNumber > 1 && colNumber <= dates.length + 1) {
      if (isWeekend(dates[colNumber - 2])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }
    if (colNumber === totalColumns - 3) cell.fill = solidFill(COLORS.blueSummary)
    if (colNumber === totalColumns - 2) cell.fill = solidFill(COLORS.greenSummary)
    if (colNumber === totalColumns - 1) cell.fill = solidFill(COLORS.emeraldSummary)
    if (colNumber === totalColumns) cell.fill = solidFill(COLORS.graySummary)
  })

  // --- Header row 2: day numbers ---
  const header2Data: (string | number)[] = ['Должность']
  for (const date of dates) {
    header2Data.push(date.getDate())
  }
  header2Data.push('', '', '', '')
  const header2 = worksheet.addRow(header2Data)
  header2.font = { bold: true, size: 9 }
  header2.height = 18

  header2.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = thinBorder()
    cell.fill = solidFill(COLORS.headerBg)

    if (colNumber > 1 && colNumber <= dates.length + 1) {
      if (isWeekend(dates[colNumber - 2])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }
    if (colNumber === totalColumns - 3) cell.fill = solidFill(COLORS.blueSummary)
    if (colNumber === totalColumns - 2) cell.fill = solidFill(COLORS.greenSummary)
    if (colNumber === totalColumns - 1) cell.fill = solidFill(COLORS.emeraldSummary)
    if (colNumber === totalColumns) cell.fill = solidFill(COLORS.graySummary)
  })

  // --- Employee rows ---
  for (const employee of employees) {
    const nameWithPosition = employee.position?.name
      ? `${employee.last_name} ${employee.first_name} (${employee.position.name})`
      : `${employee.last_name} ${employee.first_name}`
    const rowData: (string | number)[] = [nameWithPosition]

    for (const date of dates) {
      const day = findScheduleDay(employee, date)
      rowData.push(day ? getCellText(day) : '-')
    }

    rowData.push(
      employee.staffing_shifts ?? '—',
      employee.total_working_days,
      employee.actual_shifts ?? 0,
      employee.total_days_off,
    )

    const row = worksheet.addRow(rowData)
    row.height = 22

    row.eachCell((cell, colNumber) => {
      cell.border = thinBorder()

      if (colNumber === 1) {
        // Employee name
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true }
        cell.font = { size: 10 }
      } else if (colNumber > 1 && colNumber <= dates.length + 1) {
        // Day cells
        const date = dates[colNumber - 2]
        const day = findScheduleDay(employee, date)
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.font = { size: 8 }

        if (day) {
          cell.fill = solidFill(getCellColor(day))
          if (day.schedule_type === 'night_shift' && day.is_working_day) {
            cell.font = { size: 8, color: { argb: COLORS.indigoText } }
          }
        } else {
          cell.fill = solidFill(COLORS.gray)
        }
      } else if (colNumber === totalColumns - 3) {
        // ШР (штатный график 1С)
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = solidFill(COLORS.blueSummary)
        cell.font = { size: 10, bold: true, color: { argb: COLORS.blueText } }
      } else if (colNumber === totalColumns - 2) {
        // Раб.
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = solidFill(COLORS.greenSummary)
        cell.font = { size: 10, bold: true, color: { argb: COLORS.greenText } }
      } else if (colNumber === totalColumns - 1) {
        // Факт (отработано по отметкам)
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = solidFill(COLORS.emeraldSummary)
        cell.font = { size: 10, bold: true, color: { argb: COLORS.emeraldText } }
      } else if (colNumber === totalColumns) {
        // Вых.
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = solidFill(COLORS.graySummary)
        cell.font = { size: 10 }
      }
    })
  }

  // --- Summary rows ---
  if (employees.length > 0) {
    // Итоги по колонкам ШР / Раб. / Факт / Вых.
    const columnTotals = employees.reduce(
      (acc, e) => {
        acc.staffing += e.staffing_shifts ?? 0
        acc.working += e.total_working_days
        acc.actual += e.actual_shifts ?? 0
        acc.daysOff += e.total_days_off
        return acc
      },
      { staffing: 0, working: 0, actual: 0, daysOff: 0 },
    )

    // Day shift count row
    const dayShiftData: (string | number)[] = ['На смене (день)']
    for (const date of dates) {
      const dateStr = formatDateStr(date)
      let count = 0
      for (const emp of employees) {
        const day = emp.schedule.find((s) => s.date === dateStr)
        if (day?.is_working_day && day.schedule_type !== 'night_shift') count++
      }
      dayShiftData.push(count)
    }
    dayShiftData.push(
      columnTotals.staffing,
      columnTotals.working,
      columnTotals.actual,
      columnTotals.daysOff,
    )
    const dayShiftRow = worksheet.addRow(dayShiftData)
    dayShiftRow.height = 20
    dayShiftRow.font = { bold: true, size: 9 }
    dayShiftRow.eachCell((cell, colNumber) => {
      cell.border = thinBorder()
      cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'center', vertical: 'middle' }
      cell.fill = solidFill(COLORS.headerBg)
      if (colNumber > 1 && colNumber <= dates.length + 1) {
        const val = cell.value as number
        if (val > 0) cell.font = { bold: true, size: 9, color: { argb: COLORS.greenText } }
      }
      if (colNumber === totalColumns - 3) {
        cell.fill = solidFill(COLORS.blueSummary)
        cell.font = { bold: true, size: 10, color: { argb: COLORS.blueText } }
      }
      if (colNumber === totalColumns - 2) {
        cell.fill = solidFill(COLORS.greenSummary)
        cell.font = { bold: true, size: 10, color: { argb: COLORS.greenText } }
      }
      if (colNumber === totalColumns - 1) {
        cell.fill = solidFill(COLORS.emeraldSummary)
        cell.font = { bold: true, size: 10, color: { argb: COLORS.emeraldText } }
      }
      if (colNumber === totalColumns) {
        cell.fill = solidFill(COLORS.graySummary)
        cell.font = { bold: true, size: 10 }
      }
    })

    // Night shift count row
    const nightShiftData: (string | number)[] = ['На смене (ночь)']
    for (const date of dates) {
      const dateStr = formatDateStr(date)
      let count = 0
      for (const emp of employees) {
        const day = emp.schedule.find((s) => s.date === dateStr)
        if (day?.is_working_day && day.schedule_type === 'night_shift') count++
      }
      nightShiftData.push(count)
    }
    nightShiftData.push('', '', '', '')
    const nightShiftRow = worksheet.addRow(nightShiftData)
    nightShiftRow.height = 20
    nightShiftRow.font = { bold: true, size: 9 }
    nightShiftRow.eachCell((cell, colNumber) => {
      cell.border = thinBorder()
      cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'center', vertical: 'middle' }
      cell.fill = solidFill(COLORS.indigo)
      if (colNumber === 1) cell.font = { bold: true, size: 9, color: { argb: COLORS.indigoText } }
      if (colNumber > 1 && colNumber <= dates.length + 1) {
        const val = cell.value as number
        if (val > 0) cell.font = { bold: true, size: 9, color: { argb: COLORS.indigoText } }
      }
    })
  }

  // --- Column widths ---
  worksheet.getColumn(1).width = 30
  for (let i = 2; i <= dates.length + 1; i++) {
    worksheet.getColumn(i).width = 12
  }
  worksheet.getColumn(totalColumns - 3).width = 6
  worksheet.getColumn(totalColumns - 2).width = 6
  worksheet.getColumn(totalColumns - 1).width = 6
  worksheet.getColumn(totalColumns).width = 6

  // --- Legend ---
  worksheet.addRow([])
  const legendRowNum = worksheet.rowCount + 1

  const legendItems = [
    { text: 'Рабочий день', color: COLORS.green },
    { text: 'Ночная смена', color: COLORS.indigo },
    { text: 'Выходной', color: COLORS.gray },
    { text: 'Праздник', color: COLORS.red },
    { text: 'Сокращённый', color: COLORS.yellow },
    { text: 'Исключение', color: COLORS.amber },
  ]

  let legendCol = 1
  for (const item of legendItems) {
    const cell = worksheet.getCell(legendRowNum, legendCol)
    cell.value = item.text
    cell.fill = solidFill(item.color)
    cell.border = thinBorder()
    cell.alignment = { horizontal: 'center' }
    cell.font = { size: 9 }
    legendCol += 2
  }

  // --- Generate and download ---
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const filename = `График_${departmentName}_${MONTH_NAMES[month]}_${year}.xlsx`
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

// --- Helpers ---

function thinBorder(): Partial<ExcelJS.Borders> {
  return {
    top: { style: 'thin', color: { argb: 'D1D5DB' } },
    left: { style: 'thin', color: { argb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
    right: { style: 'thin', color: { argb: 'D1D5DB' } },
  }
}

function solidFill(color: string): ExcelJS.Fill {
  return {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: color },
  }
}
